import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import client from '../api/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calculator, ArrowRight, ArrowLeft, Check, Flame, Beef, Wheat, Droplets, Sparkles } from 'lucide-react';
import { adaptiveMacrosApi } from '../api/adaptiveMacros';

export default function MacroSetupWizard({ open, onClose, clientId = null }) {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Step 1 fields
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');

  // Step 2 fields
  const [activityLevel, setActivityLevel] = useState('');
  const [objective, setObjective] = useState('');

  // Step 3 — mode selection + adaptive config
  const [mode, setMode] = useState('static'); // 'static' | 'adaptive'
  const [aggressiveness, setAggressiveness] = useState('moderate'); // slow | moderate | aggressive

  // Pre-fill from user profile
  useEffect(() => {
    if (open && user) {
      setWeight(user.weight_kg || user.weight || '');
      setHeight(user.height_cm || user.height || '');
      setAge(user.age || '');
      setGender(user.gender || '');
      setStep(1);
      setResult(null);
      setMode('static');
      setAggressiveness('moderate');
    }
  }, [open, user]);

  const handleCalculate = async () => {
    if (!activityLevel || !objective) {
      toast.error(t('macroWizard.fillAllFields'));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        weight_kg: parseFloat(weight),
        height_cm: parseFloat(height),
        age: parseInt(age),
        gender,
        activity_level: activityLevel,
        objective,
        dry_run: true,
      };
      if (clientId) payload.client_id = clientId;
      const res = await client.post('/diet/calculate-macros', payload);
      setResult(res.data);
      setStep(3);
    } catch (err) {
      toast.error(t('macroWizard.calcError'));
    } finally {
      setLoading(false);
    }
  };

  const objectiveToGoal = (obj) => {
    if (obj === 'lose_weight') return 'cut';
    if (obj === 'gain_muscle') return 'bulk';
    return 'maintain';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === 'adaptive') {
        await adaptiveMacrosApi.enable({
          goal: objectiveToGoal(objective),
          aggressiveness,
          client_id: clientId || undefined,
        });
      } else {
        const payload = {
          weight_kg: parseFloat(weight),
          height_cm: parseFloat(height),
          age: parseInt(age),
          gender,
          activity_level: activityLevel,
          objective,
        };
        if (clientId) payload.client_id = clientId;
        await client.post('/diet/calculate-macros', payload);
      }
      await refreshUser();
      toast.success(t('macroWizard.saved'));
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('macroWizard.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const canProceedStep1 = weight && height && age && gender;

  const stepBarSegment = (idx) => {
    const isActive = step >= idx;
    return (
      <div
        key={idx}
        className={`h-1.5 flex-1 rounded-full transition-colors ${isActive ? 'bg-primary' : 'bg-muted'}`}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Calculator className="h-5 w-5 text-primary" />
            {t('macroWizard.title')}
          </DialogTitle>
        </DialogHeader>

        {/* Step Progress Bar */}
        <div className="flex gap-1.5 mb-2">
          {[1, 2, 3].map(stepBarSegment)}
        </div>

        {/* Step 1 — Body Info */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('macroWizard.step1Desc')}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('macroWizard.weight')} (kg)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="75"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t('macroWizard.height')} (cm)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="175"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t('macroWizard.age')}</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="25"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t('macroWizard.gender')}</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('macroWizard.gender')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('macroWizard.male')}</SelectItem>
                    <SelectItem value="female">{t('macroWizard.female')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  if (!canProceedStep1) {
                    toast.error(t('macroWizard.fillAllFields'));
                    return;
                  }
                  setStep(2);
                }}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                {t('common.next')} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — Activity & Goal */}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('macroWizard.step2Desc')}</p>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t('macroWizard.activityLevel')}</Label>
                <Select value={activityLevel} onValueChange={setActivityLevel}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('macroWizard.activityLevel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sedentary">{t('macroWizard.sedentary')}</SelectItem>
                    <SelectItem value="lightly_active">{t('macroWizard.lightlyActive')}</SelectItem>
                    <SelectItem value="moderately_active">{t('macroWizard.moderatelyActive')}</SelectItem>
                    <SelectItem value="very_active">{t('macroWizard.veryActive')}</SelectItem>
                    <SelectItem value="extremely_active">{t('macroWizard.extremelyActive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('macroWizard.objective')}</Label>
                <Select value={objective} onValueChange={setObjective}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('macroWizard.objective')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lose_weight">{t('macroWizard.loseWeight')}</SelectItem>
                    <SelectItem value="maintain">{t('macroWizard.maintain')}</SelectItem>
                    <SelectItem value="gain_muscle">{t('macroWizard.gainMuscle')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> {t('common.back')}
              </Button>
              <Button
                onClick={handleCalculate}
                disabled={loading}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                {loading ? t('common.loading') : t('macroWizard.calculate')} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — Review */}
        {step === 3 && result && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('macroWizard.step3Desc')}</p>
            <div className="grid grid-cols-2 gap-3">
              {/* Calories */}
              <div className="rounded-xl border border-primary/30 bg-primary/10 p-4 text-center">
                <Flame className="h-6 w-6 text-primary mx-auto mb-1" />
                <div className="text-2xl font-bold font-mono">{Math.round(result.calories)}</div>
                <div className="text-xs text-muted-foreground">kcal</div>
              </div>
              {/* Protein */}
              <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-center">
                <Beef className="h-6 w-6 text-red-500 mx-auto mb-1" />
                <div className="text-2xl font-bold font-mono">{Math.round(result.protein)}g</div>
                <div className="text-xs text-muted-foreground">{t('diet.protein')}</div>
              </div>
              {/* Carbs */}
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-center">
                <Wheat className="h-6 w-6 text-amber-500 mx-auto mb-1" />
                <div className="text-2xl font-bold font-mono">{Math.round(result.carbs)}g</div>
                <div className="text-xs text-muted-foreground">{t('diet.carbs')}</div>
              </div>
              {/* Fat */}
              <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 text-center">
                <Droplets className="h-6 w-6 text-blue-500 mx-auto mb-1" />
                <div className="text-2xl font-bold font-mono">{Math.round(result.fat)}g</div>
                <div className="text-xs text-muted-foreground">{t('diet.fat')}</div>
              </div>
            </div>
            {/* BMR / TDEE */}
            <div className="flex justify-center gap-6 text-sm text-muted-foreground">
              <span>BMR: <strong className="text-foreground">{Math.round(result.bmr)}</strong> kcal</span>
              <span>TDEE: <strong className="text-foreground">{Math.round(result.tdee)}</strong> kcal</span>
            </div>

            {/* Mode selection — static vs adaptive */}
            <div className="space-y-2 pt-2 border-t border-border/50">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('macroWizard.trackingMode')}</Label>
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={() => setMode('static')}
                  className={`text-left rounded-lg border min-h-[44px] p-3.5 transition-colors active:scale-[0.99] ${mode === 'static' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 ${mode === 'static' ? 'border-primary bg-primary' : 'border-muted-foreground'}`}>
                      {mode === 'static' && <div className="h-full w-full rounded-full bg-primary-foreground/20" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm flex items-center gap-1.5">
                        <Calculator className="h-3.5 w-3.5 text-primary" />
                        {t('macroWizard.modeStatic')}
                      </div>
                      <div className="text-xs text-muted-foreground">{t('macroWizard.modeStaticDesc')}</div>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('adaptive')}
                  className={`text-left rounded-lg border min-h-[44px] p-3.5 transition-colors active:scale-[0.99] ${mode === 'adaptive' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 ${mode === 'adaptive' ? 'border-primary bg-primary' : 'border-muted-foreground'}`}>
                      {mode === 'adaptive' && <div className="h-full w-full rounded-full bg-primary-foreground/20" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-[#FF5C00]" />
                        {t('macroWizard.modeAdaptive')}
                      </div>
                      <div className="text-xs text-muted-foreground">{t('macroWizard.modeAdaptiveDesc')}</div>
                    </div>
                  </div>
                </button>
              </div>

              {mode === 'adaptive' && (
                <div className="space-y-1.5 pt-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('macroWizard.adaptivePace')}</Label>
                  <Select value={aggressiveness} onValueChange={setAggressiveness}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="slow">{t('macroWizard.paceSlow')}</SelectItem>
                      <SelectItem value="moderate">{t('macroWizard.paceModerate')}</SelectItem>
                      <SelectItem value="aggressive">{t('macroWizard.paceAggressive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> {t('common.back')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                {saving ? t('common.saving') : t('macroWizard.saveTargets')} <Check className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
